import { useMemo, type ReactNode } from 'react'

import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { ShareModal, type TShareModalProps } from '@/src/features/share/ui/components/ShareModal'
import { UTM } from '@/src/general/constants/utm'
import { useUrlBuilder } from '@/src/general/hooks/useUrlBuilder'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { INTENTION_TYPE } from '../../../models/Intention.enums'
import type { TIntention } from '../../../models/Intention.types'
import { IntentionCard } from '../IntentionCard'

type TIntentionShare = Pick<TShareModalProps, 'trackingSource' | 'children'> & {
  intention?: TIntention
  intentionId: number
  shareToken?: string
}

export const IntentionShareModal = ({
  children,
  intention,
  intentionId,
  shareToken,
  trackingSource,
}: TIntentionShare): ReactNode => {
  const { t } = useTranslate()
  const { data: me } = useMeQuery()
  const { routes } = useRoutes()
  const { withUTM } = useUrlBuilder()

  const longUrl = useMemo(
    () =>
      withUTM({
        route: routes.intentionById(intentionId, shareToken ?? ''),
        campaign: UTM.CAMPAIGN.INTENTION(intentionId),
      }),
    [intentionId, shareToken, routes, withUTM],
  )

  const getShareData = async () => {
    return {
      url: longUrl,
      text:
        intention && me?.intention?.id === intentionId
          ? t('share.my.intention.email-body', { intentionUrl: longUrl })
          : t('share.intention.email-body', { intentionUrl: longUrl }),
    }
  }

  return (
    <ShareModal
      title={t('share.intention.share')}
      description={<IntentionCard intention={intention} type={INTENTION_TYPE.INTENTION} className="shadow-none" />}
      trackingSource={trackingSource}
      getShareData={getShareData}
    >
      {children}
    </ShareModal>
  )
}
